import { useSelector } from "react-redux";
import { State } from "@App/state";
import { useContext, useEffect, useMemo, useState } from "react";
import { GarageProperty } from "@App/apis/graphql/perimeterSearch/responses/garageProperty";
import { PerimeterSearchContext } from "@App/contexts/apis/PerimeterSearchContext";

/**
 * Custom hook to manage and provide garage property-related data.
 * Fetches garage properties based on the current network and service,
 * and generates filter items for use in the application.
 *
 * @returns {Object} An object containing:
 * - `availableGarageProperties`: The list of fetched garage properties or null if not available.
 * - `availableFilterItems`: The list of filter items derived from the garage properties.
 */
export const useGaragePropertyContext = () => {
  // Access the PerimeterSearch API context
  const perimeterSearchApi = useContext(PerimeterSearchContext);

  // State to store the available garage properties
  const [availableGarageProperties, setGaragePropertyFilters] = useState<GarageProperty[] | null>(
    null,
  );

  // Select the current network and service from the Redux store
  const network = useSelector((state: State) => state.orders.network);
  const service = useSelector((state: State) => state.orders.service);

  useEffect(() => {
    /**
     * Fetches garage properties from the PerimeterSearch API
     * based on the current network and service.
     */
    const fetchGarageProperties = async () => {
      if (!perimeterSearchApi || !service || !network) {
        return;
      }
      const response = await perimeterSearchApi.getGarageProperties({
        networks: [network.id],
        services: [service.id],
        showInFilter: true,
      });
      setGaragePropertyFilters(response.data);
    };
    fetchGarageProperties();
  }, [service, network]);

  // Memoized computation of filter items based on available garage properties
  const availableFilterItems = useMemo(
    () =>
      availableGarageProperties?.map((filter: GarageProperty) => ({
        id: filter.id,
        name: filter.filter_text ?? "",
        disabled: false,
      })) ?? [],
    [availableGarageProperties],
  );

  return {
    availableGarageProperties,
    availableFilterItems,
  };
};
